/* =============================================================================
   Valente — theme JS  (Shopify port of the prototype's js/ui.js)
   -----------------------------------------------------------------------------
   Progressive enhancement over the Liquid-rendered DOM. Behavior is identical to
   the prototype: sticky header, scroll-reveal, mega-menu, mobile drawer,
   center-focus coverflow, category pill filter. Differences from the prototype:
     - runs on DOMContentLoaded (no include.js / valente:ready assembler);
     - the coverflow caption reads each item's data-* (Liquid-rendered), not a
       JS data array;
     - quick-add uses Shopify's native /cart/add.js (cart is not rebuilt);
     - the newsletter is a native Shopify form, so there is no JS override.
   Everything degrades gracefully and honors prefers-reduced-motion.
   ============================================================================ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    stickyHeader();
    scrollReveal();
    megaMenu();
    mobileDrawer();
    productCarousel();
    collectionTabs();
    quickAdd();
    collectionPage();
    contactPage();
    cartPage();
    predictiveSearch();
    cookieConsent();
  }

  /* ---- Cookie consent (essential cookies only) -------------------------- */
  /* Shows the banner on first visit and remembers the choice in localStorage.
     Built so category based opt-in can be added later without changing markup. */
  function cookieConsent() {
    var el = document.querySelector('[data-cookie-consent]');
    if (!el) return;
    var KEY = 'valente:cookie-consent';
    var stored = null;
    try { stored = window.localStorage.getItem(KEY); } catch (e) { stored = null; }
    if (stored === 'accepted') return;
    el.hidden = false;
    var btn = el.querySelector('[data-cookie-accept]');
    if (btn) btn.addEventListener('click', function () {
      try { window.localStorage.setItem(KEY, 'accepted'); } catch (e) {}
      el.hidden = true;
    });
  }

  /* ---- Sticky header condenses on scroll -------------------------------- */
  function stickyHeader() {
    var header = document.querySelector('.header');
    if (!header) return;
    var ticking = false;
    function update() {
      header.classList.toggle('is-condensed', window.scrollY > 24);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---- Scroll-reveal on section entry ----------------------------------- */
  function scrollReveal() {
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (!items.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    document.documentElement.classList.add('reveal-on');

    function reveal(el) { el.classList.add('is-visible'); }
    function inView(el) {
      var r = el.getBoundingClientRect();
      return r.top < (window.innerHeight || 0) * 0.92 && r.bottom > 0;
    }
    items.forEach(function (el) { if (inView(el)) reveal(el); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    items.forEach(function (el) { if (!el.classList.contains('is-visible')) io.observe(el); });

    window.setTimeout(function () {
      items.forEach(function (el) { if (!el.classList.contains('is-visible')) reveal(el); });
    }, 1400);
  }

  /* ---- Desktop mega-menu: click toggle + keyboard + outside/Escape ------ */
  function megaMenu() {
    var item = document.querySelector('.header__nav-item');
    if (!item) return;
    var trigger = item.querySelector('.nav-trigger');
    if (!trigger) return;

    function open(state) {
      item.classList.toggle('is-open', state);
      trigger.setAttribute('aria-expanded', String(state));
    }
    trigger.addEventListener('click', function () {
      open(!item.classList.contains('is-open'));
    });
    item.addEventListener('mouseenter', function () { trigger.setAttribute('aria-expanded', 'true'); });
    item.addEventListener('mouseleave', function () { if (!item.classList.contains('is-open')) trigger.setAttribute('aria-expanded', 'false'); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && item.classList.contains('is-open')) { open(false); trigger.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (!item.contains(e.target)) open(false);
    });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) open(false);
    });
  }

  /* ---- Mobile drawer + accordion, with a light focus trap --------------- */
  function mobileDrawer() {
    var burger  = document.querySelector('.burger');
    var drawer  = document.querySelector('.drawer');
    var overlay = document.querySelector('.overlay');
    var closeBtn = drawer && drawer.querySelector('.drawer__close');
    if (!burger || !drawer || !overlay) return;

    var lastFocus = null;

    function focusables() {
      return drawer.querySelectorAll('a[href], button:not([disabled]), input');
    }
    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.classList.add('is-locked');
      burger.setAttribute('aria-expanded', 'true');
      var f = focusables();
      if (f.length) f[0].focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      burger.setAttribute('aria-expanded', 'false');
      if (lastFocus) lastFocus.focus();
    }

    burger.addEventListener('click', open);
    overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    drawer.querySelectorAll('.acc__trigger').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ---- Principal-products coverflow: center-focus, arrows, keyboard, drag -
     Identical mechanics to the prototype; the shared caption now reads each
     item's data-* (name/desc/price/url) instead of a JS data array. */
  function productCarousel() {
    var cf = document.querySelector('.coverflow');
    if (!cf) return;
    var track = cf.querySelector('.cf__track');
    var items = Array.prototype.slice.call(cf.querySelectorAll('.cf__item'));
    if (!track || !items.length) return;

    var scope = cf.closest('section') || document;
    var prev = scope.querySelector('[data-carousel="prev"]');
    var next = scope.querySelector('[data-carousel="next"]');
    var nameEl = cf.querySelector('.cf__name');
    var descEl = cf.querySelector('.cf__desc');
    var priceEl = cf.querySelector('.cf__price');
    var linkEl = cf.querySelector('.cf__link');

    /* --- Reduced-motion fallback: plain scroll-snap row. --- */
    if (REDUCED) {
      cf.classList.add('is-static');
      var rowStep = function () {
        var it = items[0];
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return it ? it.getBoundingClientRect().width + gap : track.clientWidth;
      };
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -rowStep(), behavior: 'auto' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: rowStep(), behavior: 'auto' }); });
      return;
    }

    var n = items.length;
    var active = 0;

    function mod(i) { return ((i % n) + n) % n; }
    function itemWidth() { return items[0].offsetWidth || 240; }

    function styleFor(o) {
      var ao = Math.abs(o);
      if (o === 0)  return { scale: 1,    opacity: 1,    ty: 0,  ao: ao };
      if (ao === 1) return { scale: 0.74, opacity: 0.6,  ty: 14, ao: ao };
      if (ao === 2) return { scale: 0.55, opacity: 0.26, ty: 24, ao: ao };
      return          { scale: 0.5,  opacity: 0,    ty: 28, ao: ao };
    }

    function layout() {
      var step = itemWidth() * 0.6;
      var half = n / 2;
      items.forEach(function (item) {
        var idx = parseInt(item.getAttribute('data-index'), 10);
        var raw = mod(idx - active);
        var o = raw > half ? raw - n : raw;
        var prevOffset = item.__offset;
        var wrapped = prevOffset !== undefined && Math.abs(o - prevOffset) > half;
        item.__offset = o;

        var s = styleFor(o);
        var x = o * step;
        function apply() {
          item.style.transform =
            'translate(calc(-50% + ' + x + 'px), calc(-50% + ' + s.ty + 'px)) scale(' + s.scale + ')';
          item.style.opacity = String(s.opacity);
          item.style.zIndex = String(100 - s.ao);
          item.style.pointerEvents = s.opacity === 0 ? 'none' : 'auto';
          item.setAttribute('aria-hidden', o === 0 ? 'false' : 'true');
        }
        if (wrapped) {
          item.style.transition = 'none';
          apply();
          void item.offsetWidth;
          item.style.transition = '';
        } else {
          apply();
        }
      });
      updateCaption();
    }

    function updateCaption() {
      var item = items[mod(active)];
      if (!item) return;
      var d = item.dataset;
      if (nameEl) nameEl.textContent = d.name || '';
      if (descEl) descEl.textContent = d.desc || '';
      if (priceEl) priceEl.textContent = d.price || '';
      if (linkEl) linkEl.setAttribute('href', d.url || '#');
    }
    function move(delta) { active += delta; layout(); }

    var down = false, startX = 0, dragMoved = false;

    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    track.addEventListener('click', function (e) {
      var card = e.target.closest('.cf__card');
      if (!card || dragMoved) return;
      var item = card.closest('.cf__item');
      if (item && item.__offset) move(item.__offset);
    });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
    });

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true; dragMoved = false; startX = e.clientX;
    });
    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      if (Math.abs(e.clientX - startX) > 8) dragMoved = true;
    });
    function endDrag(e) {
      if (!down) return;
      down = false;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 40) move(dx < 0 ? 1 : -1);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', function () { down = false; });

    var rAF = false;
    window.addEventListener('resize', function () {
      if (!rAF) { window.requestAnimationFrame(function () { layout(); rAF = false; }); rAF = true; }
    }, { passive: true });

    layout();
  }

  /* ---- "Nueva colección" tabs: switch which collection grid is shown -------- */
  function collectionTabs() {
    document.querySelectorAll('[data-collection-tabs]').forEach(function (tablist) {
      var pills = Array.prototype.slice.call(tablist.querySelectorAll('[data-tab]'));
      if (!pills.length) return;
      var scope = tablist.closest('section') || document;
      // Panels live in the following section; find them by the data-tab-panel attr.
      var host = tablist.closest('.pills-band');
      var panelRoot = host && host.nextElementSibling ? host.nextElementSibling : document;
      var panels = Array.prototype.slice.call(panelRoot.querySelectorAll('[data-tab-panel]'));
      if (!panels.length) panels = Array.prototype.slice.call(document.querySelectorAll('[data-tab-panel]'));

      function show(idx) {
        pills.forEach(function (p) {
          var on = p.getAttribute('data-tab') === idx;
          p.classList.toggle('on', on);
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-selected', String(on));
        });
        panels.forEach(function (panel) { panel.hidden = panel.getAttribute('data-tab-panel') !== idx; });
      }
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () { show(pill.getAttribute('data-tab')); });
      });
    });
  }

  /* ---- Quick-add: native AJAX add to cart (/cart/add.js) ----------------- */
  function quickAdd() {
    function setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = '(' + n + ')'; });
    }
    document.querySelectorAll('[data-quick-add]').forEach(function (btn) {
      if (btn.disabled) return;
      var original = btn.textContent;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-variant-id');
        if (!id || btn.dataset.busy) return;
        btn.dataset.busy = '1';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        })
          .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
          .then(function () { btn.textContent = 'Añadido ✓'; return fetch('/cart.js'); })
          .then(function (r) { return r.json(); })
          .then(function (cart) { setCount(cart.item_count); })
          .catch(function () { btn.textContent = 'Reintentar'; })
          .then(function () {
            window.setTimeout(function () { btn.textContent = original; delete btn.dataset.busy; }, 1500);
          });
      });
    });
  }

  /* ---- Collection page: native sort, filter drawer, grid fade ------------
     Filtering + sort are real URL navigations (SEO-friendly, linkable, native);
     the active pill/sort state is server-rendered. JS only smooths the edges. */
  function collectionPage() {
    var root = document.querySelector('[data-collection]');
    if (!root) return;

    // Sort: submit the GET form on change (action already carries the tag path).
    var sortForm = root.querySelector('[data-coll-sort-form]');
    var sortSel = root.querySelector('[data-coll-sort]');
    if (sortForm && sortSel) {
      sortSel.addEventListener('change', function () { sortForm.submit(); });
    }

    // Mobile filter drawer (its own drawer/overlay; independent of the header's).
    var openBtn = root.querySelector('[data-coll-filter-open]');
    var closeBtn = root.querySelector('[data-coll-filter-close]');
    var drawer = root.querySelector('#coll-drawer');
    var overlay = root.querySelector('[data-coll-overlay]');
    if (openBtn && drawer && overlay) {
      var lastFocus = null;
      var open = function () {
        lastFocus = document.activeElement;
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
        document.body.classList.add('is-locked');
        openBtn.setAttribute('aria-expanded', 'true');
        var f = drawer.querySelector('a, button');
        if (f) f.focus();
      };
      var close = function () {
        drawer.classList.remove('is-open');
        overlay.classList.remove('is-open');
        document.body.classList.remove('is-locked');
        openBtn.setAttribute('aria-expanded', 'false');
        if (lastFocus) { lastFocus.focus(); lastFocus = null; }
      };
      openBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      overlay.addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });
    }

    // Subtle fade of the grid on load (honors reduced-motion via the CSS guard).
    if (!REDUCED) {
      var grid = root.querySelector('[data-coll-grid]');
      if (grid) { grid.classList.remove('is-fading'); void grid.offsetWidth; grid.classList.add('is-fading'); }
    }
  }

  /* ---- Contacto page: pre-select Motivo from ?motivo= -------------------
     Advisor/quote CTAs across the theme deep-link with ?motivo=asesor|cotizar.
     Match it against each option's data-motivo and select it. */
  function contactPage() {
    var select = document.querySelector('[data-contact-motivo]');
    if (!select) return;
    var motivo;
    try { motivo = new URLSearchParams(window.location.search).get('motivo'); } catch (e) { return; }
    if (!motivo) return;
    var opts = select.querySelectorAll('option');
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].getAttribute('data-motivo') === motivo) { select.value = opts[i].value; break; }
    }
  }

  /* ---- Cart: quantity steppers submit the native cart form -------------- */
  function cartPage() {
    var root = document.querySelector('[data-cart-page]');
    if (!root) return;
    root.querySelectorAll('.qty').forEach(function (group) {
      var input = group.querySelector('[data-qty-input]');
      if (!input) return;
      var form = input.closest('form');
      function submitForm() { if (form) { if (form.requestSubmit) { form.requestSubmit(); } else { form.submit(); } } }
      var down = group.querySelector('[data-qty-down]');
      var up = group.querySelector('[data-qty-up]');
      if (down) down.addEventListener('click', function () { input.value = Math.max(0, (parseInt(input.value, 10) || 0) - 1); submitForm(); });
      if (up) up.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || 0) + 1; submitForm(); });
      input.addEventListener('change', submitForm);
    });
  }

  /* ---- Predictive search overlay (native /search/suggest.json) ----------
     The header search util keeps its /search href as a no-JS fallback. */
  function predictiveSearch() {
    var modal = document.querySelector('[data-search-modal]');
    var openBtns = document.querySelectorAll('[data-search-open]');
    if (!modal || !openBtns.length) return;
    var input = modal.querySelector('[data-predictive-input]');
    var results = modal.querySelector('[data-predictive-results]');
    var lastFocus = null, timer = null, controller = null;

    function open(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('is-locked');
      if (input) input.focus();
    }
    function close() {
      modal.hidden = true;
      document.body.classList.remove('is-locked');
      if (results) results.innerHTML = '';
      if (lastFocus) { lastFocus.focus(); lastFocus = null; }
    }
    openBtns.forEach(function (b) { b.addEventListener('click', open); });
    modal.querySelectorAll('[data-search-close]').forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !modal.hidden) close(); });

    function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
    function render(products, q) {
      if (!results) return;
      if (!products.length) { results.innerHTML = '<p class="ps-empty">No encontramos piezas para tu búsqueda.</p>'; return; }
      var html = products.map(function (p) {
        var img = (p.featured_image && p.featured_image.url) || p.image || '';
        var thumb = img ? '<span class="ps-item__media"><img src="' + esc(img) + '" alt="" loading="lazy"></span>' : '<span class="ps-item__media"></span>';
        return '<a class="ps-item" href="' + esc(p.url) + '">' + thumb +
          '<span><span class="ps-item__name">' + esc(p.title) + '</span><br><span class="ps-item__price">' + esc(p.price) + '</span></span></a>';
      }).join('');
      html += '<a class="ps-all" href="' + '/search?q=' + encodeURIComponent(q) + '">Ver todos los resultados →</a>';
      results.innerHTML = html;
    }
    function query(q) {
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6';
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          render(products, q);
        })
        .catch(function () {});
    }
    if (input) input.addEventListener('input', function () {
      var q = input.value.trim();
      if (timer) window.clearTimeout(timer);
      if (q.length < 2) { if (results) results.innerHTML = ''; return; }
      timer = window.setTimeout(function () { query(q); }, 220);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
